// src/utils/liveTicketStore.js
// ─────────────────────────────────────────────────────────────────────────────
// What this browser keeps to draw a live ticket QR (16 Oct 2026,
// src/utils/liveTicket.js). The apps keep the same in the phone's keychain
// (app/components/ticketing/liveTicketStore.js).
//
//   the device id   random, made once, sent as `x-ticket-device` so the server
//                   can bind a ticket to THIS browser (it stores only a hash)
//   display keys    one per ticket this browser holds, with the clock offset
//                   measured when it arrived, so the QR draws with no network
//
// A browser claims a ticket only when the customer asks ("Show it on this
// device"), never just by opening it: a ticket bought on a laptop must not
// end up tied to the laptop when the customer will be at the door with their
// phone. The phone app claims on first view; this page offers the move.
//
// localStorage, like the session token and the offline ticket copy
// (ticketCache.js): a browser has nothing safer that survives a reload. A key
// read out of it still admits only for the minute it is drawn, once.
// ─────────────────────────────────────────────────────────────────────────────

const DEVICE_KEY = "lusuku:ticket-device:v1";
const keyName = (ticketId) => `lusuku:live:v1:${String(ticketId).replace(/[^A-Za-z0-9_.-]/g, "")}`;
const DEVICE_ID = /^[a-f0-9]{32,64}$/i;

// Safari in private mode throws on every localStorage access, and a full
// origin throws on write. Neither may take the ticket page down.
const readRaw = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeRaw = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};
const removeRaw = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* nothing stored */
  }
};

const randomHex = (bytes) => {
  const buf = new Uint8Array(bytes);
  if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(buf);
  else for (let i = 0; i < bytes; i += 1) buf[i] = Math.floor(Math.random() * 256);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
};

let sessionDeviceId = null;

/** This browser's ticket device id: made once, then the same. */
export const getTicketDeviceId = () => {
  const existing = readRaw(DEVICE_KEY);
  if (existing && DEVICE_ID.test(existing)) return existing;
  // Not kept (private mode): one id for this tab's life, so the move still works.
  if (!sessionDeviceId) sessionDeviceId = randomHex(16);
  writeRaw(DEVICE_KEY, sessionDeviceId);
  return sessionDeviceId;
};

/** Keep the key a ticket arrived with, and how far this device's clock is off. */
export const saveLiveKey = (ticketId, live) => {
  if (!ticketId || !live || !live.key) return null;
  const record = {
    key: live.key,
    keyVersion: Number(live.keyVersion) || 0,
    stepSeconds: Number(live.stepSeconds) || 15,
    codeSeconds: Number(live.codeSeconds) || 60,
    offsetMs: Number.isFinite(Number(live.serverTime)) ? Number(live.serverTime) - Date.now() : 0,
    savedAt: Date.now(),
  };
  writeRaw(keyName(ticketId), JSON.stringify(record));
  return record;
};

/** The stored key for a ticket, or null. */
export const loadLiveKey = (ticketId) => {
  if (!ticketId) return null;
  try {
    const parsed = JSON.parse(readRaw(keyName(ticketId)) || "null");
    return parsed && parsed.key ? parsed : null;
  } catch (e) {
    return null;
  }
};

/** Whether this browser holds the ticket (and so may name itself when asking). */
export const holdsTicket = (ticketId) => Boolean(loadLiveKey(ticketId));

/** Forget a ticket's key: it moved to another device, or can no longer be used. */
export const forgetLiveKey = (ticketId) => {
  if (ticketId) removeRaw(keyName(ticketId));
};

/**
 * Bring the stored key in line with what the server just said about a ticket
 * this browser asked about by its device id.
 * @returns the record to draw from, or null
 */
export const syncLiveKey = (ticket) => {
  if (!ticket || !ticket._id) return null;
  if (ticket.live && ticket.live.key) return saveLiveKey(ticket._id, ticket.live);
  const ended = ticket.status && ticket.status !== "VALID";
  if (ticket.liveHeldElsewhere || ended) forgetLiveKey(ticket._id);
  return null;
};
